import React, { useEffect, useRef, useState } from "react";
import { View } from "react-native";
import { router, useFocusEffect } from "expo-router";
import { StyleSheet } from "@/utils/appStyleSheet";
import { LoadingScreen } from "@/components/dashboard/HomeScreenComponents";
import { ClaimTab, ClaimHistoryTab } from "@/components/claims";
import type { ClaimTabKey } from "@/components/claims";
import SettingTipSection from "@/components/SettingTipSection";
import { UnderlineTabBar } from "@/components/ui/UnderlineTabBar";
import { useFeedData } from "@/hooks/useFeedData";
import useWindow from "@/hooks/useWindow";
import FBTrackingService from "@/services/fbTrackingService";
import useUserStore from "@/store/user";
import { isWeb } from "@/utils/platform";

const TAB_OPTIONS = [
  { key: "all-claims", emoji: "💸", label: "All claims" },
  { key: "settlements", emoji: "🏠", label: "Settlements" },
  { key: "signup-offers", emoji: "💳", label: "Signup Offers" },
  { key: "history", emoji: "🕰️", label: "History" },
];

export default function HomeScreen() {
  const { isDesktop } = useWindow();
  const [selectedTab, setSelectedTab] = useState("all-claims");
  const { isLoading } = useFeedData();
  const { isFreePlan, userProfile } = useUserStore();
  const hasNavigated = useRef(false);

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("my-claims");
    }, []),
  );

  useEffect(() => {
    if (!isWeb && userProfile && isFreePlan && !hasNavigated.current) {
      hasNavigated.current = true;
      router.replace("/signup/onboard-paywall");
    }
  }, [userProfile, isFreePlan]);

  if (isLoading || (userProfile && isFreePlan)) {
    return <LoadingScreen />;
  }

  return (
    <View style={styles.container}>
      <View
        style={[styles.listContainer, isDesktop && styles.listContainerDesktop]}
      >
        <View style={[styles.content, isDesktop && styles.contentDesktop]}>
          <UnderlineTabBar
            tabs={TAB_OPTIONS}
            activeKey={selectedTab}
            onTabSelect={setSelectedTab}
          />

          {/* Tab Content */}
          {selectedTab !== "history" ? (
            <ClaimTab selectedTab={selectedTab as ClaimTabKey} />
          ) : (
            <ClaimHistoryTab isActive />
          )}
        </View>
        {isDesktop && (
          <View style={styles.settingTipSectionDesktop}>
            <SettingTipSection />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  listContainerDesktop: {
    flexDirection: "row",
  },
  content: {
    flex: 1,
  },
  contentDesktop: {
    flex: 1.5,
    alignItems: "center",
  },
  settingTipSectionDesktop: {
    marginTop: 24,
    marginRight: 24,
  },
});
